import { supabase } from "./client";
import type { GEntry, GSprint } from "../types/entries";

// ---- Entry mapping ----

function entryToRow(userId: string, e: GEntry & { tdeeSource?: string | null }) {
  return {
    user_id: userId,
    date: e.date,
    weight: e.weight,
    calories_in: e.caloriesIn,
    active_calories: e.activeCalories,
    total_burned: e.totalBurned,
    net_calories: e.netCalories,
    bmr: e.bmr,
    notes: e.notes,
    source: e.source,
    tdee_source: e.tdeeSource ?? null,
    updated_at: e.updatedAt,
    active_calories_source: e.activeCaloriesSource ?? null,
    total_burned_source: e.totalBurnedSource ?? null,
    active_calories_rejected_reason: e.activeCaloriesRejectedReason ?? null,
    total_burned_rejected_reason: e.totalBurnedRejectedReason ?? null,
  };
}

function rowToEntry(row: any): GEntry & { tdeeSource?: string | null } {
  return {
    date: row.date,
    weight: row.weight,
    caloriesIn: row.calories_in,
    activeCalories: row.active_calories,
    totalBurned: row.total_burned,
    netCalories: row.net_calories,
    bmr: row.bmr,
    notes: row.notes,
    source: row.source,
    tdeeSource: row.tdee_source ?? null,
    updatedAt: row.updated_at,
    activeCaloriesSource: row.active_calories_source ?? null,
    totalBurnedSource: row.total_burned_source ?? null,
    activeCaloriesRejectedReason: row.active_calories_rejected_reason ?? null,
    totalBurnedRejectedReason: row.total_burned_rejected_reason ?? null,
  };
}

// ---- Sprint mapping ----

function sprintToRow(userId: string, s: GSprint) {
  return {
    user_id: userId,
    sprint_id: s.id,
    name: s.name,
    start_date: s.startDate,
    end_date: s.endDate ?? null,
    color: s.color,
    roadmap_id: s.roadmapId ?? null,
    phase_id: s.phaseId ?? null,
    phase_order_index: s.phaseOrderIndex ?? null,
    created_at: s.createdAt ?? null,
    updated_at: s.updatedAt ?? null,
  };
}

function rowToSprint(row: any): GSprint {
  return {
    id: row.sprint_id,
    name: row.name,
    startDate: row.start_date,
    endDate: row.end_date ?? null,
    color: row.color ?? "#60a5fa",
    roadmapId: row.roadmap_id ?? undefined,
    phaseId: row.phase_id ?? undefined,
    phaseOrderIndex: row.phase_order_index ?? undefined,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  };
}

// ---- Pull ----

export function hasMeaningfulProfileVitals(vitals: any) {
  if (!vitals || typeof vitals !== "object") return false;
  return (
    Number.isFinite(vitals.height)
    || Number.isFinite(vitals.currentWeightLbs)
    || (typeof vitals.gender === "string" && vitals.gender.trim().length > 0)
    || (typeof vitals.birthday === "string" && vitals.birthday.trim().length > 0)
  );
}

export async function pullAll(userId: string) {
  const [entriesRes, sprintsRes, profileRes] = await Promise.all([
    supabase.from("entries").select("*").eq("user_id", userId),
    supabase.from("sprints").select("*").eq("user_id", userId),
    supabase.from("profiles").select("*").eq("id", userId).maybeSingle(),
  ]);

  if (entriesRes.error) throw new Error(entriesRes.error.message);
  if (sprintsRes.error) throw new Error(sprintsRes.error.message);
  if (profileRes.error) throw new Error(profileRes.error.message);

  return {
    entries: (entriesRes.data ?? []).map(rowToEntry),
    sprints: (sprintsRes.data ?? []).map(rowToSprint),
    vitals: profileRes.data?.vitals ?? null,
    forecast: profileRes.data?.forecast ?? null,
    roadmaps: profileRes.data?.roadmaps ?? [],
    profileUpdatedAt: profileRes.data?.updated_at ?? null,
    hasProfile: !!profileRes.data,
    hasProfileVitals: hasMeaningfulProfileVitals(profileRes.data?.vitals ?? null),
    healthKitEnabled: profileRes.data?.healthkit_enabled ?? null,
  };
}

// ---- Demo dataset detection ----

const DEMO_SPRINT_IDS = new Set([
  "cut-ahead-active", "bulk-lean-active", "maintain-active",
  "hist-1", "hist-2", "hist-3", "hist-4", "hist-5", "hist-6", "hist-7",
]);
const DEMO_ROADMAP_IDS = new Set(["roadmap-cut-ahead", "roadmap-bulk-lean", "roadmap-maintain"]);

/**
 * Returns true if the dataset looks like demo data based on known demo identifiers.
 * Used as a defense-in-depth backstop to prevent demo data from ever reaching Supabase,
 * even if the isDemoMode flag is stale or a race condition occurs.
 */
export function isDemoDataset(
  entries: (GEntry & { tdeeSource?: string | null })[],
  sprints: GSprint[],
  roadmaps?: any[]
): boolean {
  if (entries.some((e) => e.tdeeSource === "demo")) return true;
  if (sprints.some((s) => s.id && (DEMO_SPRINT_IDS.has(String(s.id)) || String(s.id).startsWith("hist-")))) return true;
  if (roadmaps?.some((r) => r?.id && DEMO_ROADMAP_IDS.has(String(r.id)))) return true;
  return false;
}

export function isDemoSprintId(id: string): boolean {
  return DEMO_SPRINT_IDS.has(id) || id.startsWith("hist-");
}

export function isDemoRoadmapId(id: string): boolean {
  return DEMO_ROADMAP_IDS.has(id);
}

// ---- Push ----

export async function pushEntries(userId: string, entries: (GEntry & { tdeeSource?: string | null })[]) {
  if (!entries.length) return;
  const rows = entries.map((e) => entryToRow(userId, e));
  const { error } = await supabase.from("entries").upsert(rows, { onConflict: "user_id,date" });
  if (error) throw new Error(error.message);
}

export async function pushSprints(userId: string, sprints: GSprint[]) {
  if (!sprints.length) return;
  const rows = sprints.map((s) => sprintToRow(userId, s));
  const { error } = await supabase.from("sprints").upsert(rows, { onConflict: "user_id,sprint_id" });
  if (error) throw new Error(error.message);
}

export async function deleteSprintFromCloud(userId: string, sprintId: string) {
  const { error } = await supabase
    .from("sprints")
    .delete()
    .eq("user_id", userId)
    .eq("sprint_id", sprintId);
  if (error) throw new Error(error.message);
}

export async function pushProfile(userId: string, vitals: any, forecast: any, roadmaps: any[], healthKitEnabled?: boolean) {
  const payload: Record<string, unknown> = { id: userId, vitals, forecast, roadmaps, updated_at: new Date().toISOString() };
  if (healthKitEnabled !== undefined) payload.healthkit_enabled = healthKitEnabled;
  const { error } = await supabase
    .from("profiles")
    .upsert(payload, { onConflict: "id" });
  if (error) throw new Error(error.message);
}

export async function pushAll(
  userId: string,
  entries: (GEntry & { tdeeSource?: string | null })[],
  sprints: GSprint[],
  vitals: any,
  forecast: any,
  roadmaps: any[]
) {
  // Final backstop: never write demo data to Supabase regardless of caller state
  if (isDemoDataset(entries, sprints, roadmaps)) {
    console.warn("[sync] pushAll blocked — demo dataset detected. No data was written to Supabase.");
    return;
  }
  await Promise.all([
    pushEntries(userId, entries),
    pushSprints(userId, sprints),
    pushProfile(userId, vitals, forecast, roadmaps),
  ]);
}

// ---- Merge ----

export function asTs(v?: string | null) {
  if (!v || !String(v).trim()) return null;
  const n = new Date(String(v)).getTime();
  return Number.isFinite(n) ? n : null;
}

function shouldUseRemote(remoteUpdatedAt?: string | null, localUpdatedAt?: string | null) {
  const r = asTs(remoteUpdatedAt);
  const l = asTs(localUpdatedAt);
  // v1 conflict policy: remote wins when timestamps tie.
  if (r !== null && l !== null) return r >= l;
  // Prefer remote row when either timestamp is absent.
  return true;
}

/**
 * Merges a local and remote array of objects by a key, applying a
 * "remote wins on tie" conflict policy based on updatedAt timestamps.
 * Generic — works for both entries and sprints.
 */
export function mergeRemoteLocal<T extends Record<string, unknown>>(
  local: T[],
  remote: T[],
  key: (t: T) => string,
  updatedAt: (t: T) => string | null | undefined,
): T[] {
  const map = new Map<string, T>();
  for (const l of local) map.set(key(l), { ...l });

  for (const r of remote) {
    const k = key(r);
    const l = map.get(k);
    if (!l) {
      map.set(k, { ...r });
    } else {
      map.set(k, shouldUseRemote(updatedAt(r), updatedAt(l)) ? { ...r } : { ...l });
    }
  }
  return Array.from(map.values()).map((v) => ({ ...v }));
}
